import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import TutorialRestartButton from '../TutorialRestartButton';
import CoffeeChatQuestions from './CoffeeChatQuestions';
import { getTutorialSteps } from './config/tutorialConfig';
import companyToId from './LinkedInConsts/company_to_id.json';
import locationToId from './LinkedInConsts/location_to_id.json';
import schoolToId from './LinkedInConsts/school_to_id.json';

const AlumniFinder = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('Any');
  const [filteredData, setFilteredData] = useState([]);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const { userProfile, updateTutorialFlag, userSchool, trialStatus } = useContext(GlobalContext);

  // Add refs for tutorial nodes
  const nodeRefs = {
    'search-bar': useRef(null),
    'company-search': useRef(null),
    'filter-options': useRef(null),
    'search-button': useRef(null),
    'coffee-chat-questions': useRef(null),
  };

  // Initial load effect for tutorial state and results
  useEffect(() => {
    // Check localStorage for tutorial state
    if (userProfile && userProfile.onboarding_progress) {
      setShowTutorial(!userProfile.onboarding_progress.hasSeenAlumniFinderTutorial);
      console.log(
        'hasSeenAlumniFinderTutorial',
        userProfile.onboarding_progress.hasSeenAlumniFinderTutorial
      );
    }

    // Fetch initial results
    const params = new URLSearchParams();
    params.append('limit', '50');
    params.append('random_order', 'true');
    fetchResults(params);
  }, [userProfile]);

  // Add effect to handle body scroll during tutorial
  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  const handleNextStep = () => {
    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenAlumniFinderTutorial', true);
  };

  const companyData = [
    {
      category: 'Big Tech',
      items: ['Google', 'Microsoft', 'Apple', 'Meta', 'Amazon'],
      emoji: '🏢',
    },
    {
      category: 'Startups',
      items: ['Stripe', 'Databricks', 'Plaid', 'Duolingo', 'Atlassian'],
      emoji: '🚀',
    },
  ];

  const roles = [
    'Software Engineer',
    'Product Manager',
    'Data Scientist',
    'Design',
    'Engineering Manager',
  ];

  const roles_to_search_terms = {
    'Software Engineer': ['software', 'swe', 'sde'],
    'Product Manager': ['pm', 'product'],
    'Data Scientist': ['data', 'ds', 'quant', 'ml', 'machine learning'],
    Design: ['ux', 'ui', 'design', 'designer'],
    'Engineering Manager': ['engineering manager', 'em'],
  };

  const locations = ['Any', 'Bay Area', 'New York City', 'Seattle', 'Los Angeles'];

  const fetchResults = async (params) => {
    try {
      const response = await axios.get(`/api/v1/alumni?${params.toString()}`);
      setResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Error fetching alumni data:', error.response?.data?.detail || error.message);
      setResults([]);
    }
  };

  useEffect(() => {
    const filterData = () => {
      if (!searchTerm) {
        setFilteredData(companyData);
        return;
      }

      const filtered = companyData
        .map((category) => ({
          category: category.category,
          items: category.items.filter((item) =>
            item.toLowerCase().includes(searchTerm.toLowerCase())
          ),
          emoji: category.emoji,
        }))
        .filter((category) => category.items.length > 0);

      setFilteredData(filtered);
    };

    filterData();
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
  };

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  const handleItemClick = (item) => {
    setSearchTerm(item);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    // build url (sample below)
    // https://www.linkedin.com/search/results/people/?currentCompany=%5B%2210667%22%5D&keywords=software%20OR%20product&schoolFilter=%5B%2219253%22%5D
    // TODO: get school name
    let schoolName = userSchool;
    let companyLinkedinID;

    // db query to get company's linkedin id by name
    try {
      const response = await axios.get(
        `/api/v1/company/linkedin/${encodeURIComponent(searchTerm)}`
      );
      if (response.data && response.data.linkedin_id) {
        companyLinkedinID = response.data.linkedin_id;
      } else {
        console.error('Company not found in database');
        return;
      }
    } catch (error) {
      console.error(
        "Error fetching company's linkedin id:",
        error.response?.data?.detail || error.message
      );
    }
    let companyCodes = [companyLinkedinID];
    // hardcode for Amazon to include AWS as well
    if (searchTerm.toLowerCase() === 'amazon')
      companyCodes.push(companyToId['Amazon Web Services']);

    let schoolCode = schoolToId[schoolName];
    let keywords = selectedRole === 'All' ? null : roles_to_search_terms[selectedRole];
    let location = selectedLocation === 'Any' ? null : locationToId[selectedLocation];

    let url = 'https://www.linkedin.com/search/results/people/';

    let params = new URLSearchParams();
    if (companyCodes.length > 0) params.append('currentCompany', `[${companyCodes.join(',')}]`);
    if (keywords) params.append('keywords', keywords.map((term) => `"${term}"`).join(' OR '));
    if (schoolCode) params.append('schoolFilter', `[${schoolCode}]`);
    if (location) params.append('geoUrn', location);

    url += `?${params.toString()}`;

    window.open(url, '_blank');
  };

  const shortenGradYear = (gradYear) => {
    // convert 2023 to 23
    if (gradYear) return "'" + gradYear.toString().slice(2);
    return '';
  };

  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const TUTORIAL_STEPS = getTutorialSteps(trialStatus?.has_trial);
    const currentStep = TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    const rect = currentRef?.current?.getBoundingClientRect();

    if (!rect) return null;

    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      switch (currentStep.id) {
        case 'company-search':
          return {
            top: rect.bottom + 20,
            left: rect.left + rect.width / 2 - 150,
            width: '500px',
            '--arrow-orientation': 'up',
          };
        case 'filter-options':
          return {
            top: rect.bottom + 20,
            left: rect.left + rect.width / 2 - 150,
            width: '300px',
            '--arrow-orientation': 'up',
          };
        case 'search-button':
          return {
            top: rect.top + rect.height / 2 - 50,
            left: rect.left - 320,
            width: '300px',
            '--arrow-orientation': 'right',
          };
        case 'coffee-chat-questions':
          return {
            top: rect.top - 120,
            left: rect.left + rect.width / 2 - 150,
            width: '500px',
            '--arrow-orientation': 'down',
          };
        default:
          if (isSmallScreen) {
            return {
              top: rect.bottom + 20,
              left: rect.left,
              transform: 'none',
              width: Math.min(300, rect.width) + 'px',
            };
          }
          return {
            top: rect.top + rect.height / 2 - 100,
            left: currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20,
            width: '300px',
          };
      }
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
        />
      </>
    );
  };

  return (
    <div className="flex-1 flex flex-col items-center bg-gray-50 p-8">
      <div className="w-full max-w-6xl">
        <h1 className="text-3xl font-bold mb-6 text-center">
          {trialStatus?.has_trial ? 'Networking' : 'Alumni Finder'}
        </h1>
        <div className="relative">
          <div ref={nodeRefs['search-bar']} className="flex space-x-4">
            <div className="relative flex-1" ref={nodeRefs['company-search']}>
              <label
                htmlFor="company-search"
                className="block text-sm font-medium text-gray-700 mb-1 ml-2"
              >
                Company
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 inset-s-0 flex items-center pointer-events-none z-20 ps-3.5">
                  <svg
                    className="shrink-0 size-4 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.3-4.3"></path>
                  </svg>
                </div>
                <input
                  id="company-search"
                  ref={inputRef}
                  className="py-3 ps-10 pe-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex space-x-4" ref={nodeRefs['filter-options']}>
              <div>
                <label
                  htmlFor="role-select"
                  className="block text-sm font-medium text-gray-700 mb-1 ml-2"
                >
                  Role
                </label>
                <select
                  id="role-select"
                  className="py-3 px-4 pe-9 block w-50 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                  value={selectedRole}
                  onChange={handleRoleChange}
                >
                  <option value="">Any</option>
                  {roles.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="location-select"
                  className="block text-sm font-medium text-gray-700 mb-1 ml-2"
                >
                  Location
                </label>
                <select
                  id="location-select"
                  className="py-3 px-4 pe-9 block w-40 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                  value={selectedLocation}
                  onChange={handleLocationChange}
                >
                  {locations.map((location, index) => (
                    <option key={index} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-end" ref={nodeRefs['search-button']}>
              <button
                onClick={handleSubmit}
                className="py-3 px-4 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                ☕ Search
              </button>
            </div>
          </div>

          {isOpen && (
            <div
              ref={dropdownRef}
              className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
            >
              <div className="max-h-[500px] overflow-y-auto">
                {filteredData.map((category, index) => (
                  <div key={index}>
                    <div className="px-3 py-2 text-xs uppercase text-gray-500 font-semibold">
                      {category.category}
                    </div>
                    {category.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="cursor-pointer px-4 py-2 hover:bg-gray-100 text-sm"
                        onClick={() => handleItemClick(item)}
                      >
                        <div className="flex items-center">
                          <span className="text-xl me-2.5">{category.emoji}</span>
                          <span>{item}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <CoffeeChatQuestions ref={nodeRefs['coffee-chat-questions']} />
      <TutorialOverlay />
      <TutorialRestartButton tutorialKey="hasSeenAlumniFinderTutorial" />
    </div>
  );
};

export default AlumniFinder;
